/**
 * BusinessSettings — Reporting Currency selection + Logo branding for payslips & emails.
 * All state and handlers live in Settings.js (parent). This component is pure JSX.
 * RULE: Do NOT add state here. Pass new props from Settings.js instead.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Settings2, ImageIcon, Loader2 } from 'lucide-react';
import { CURRENCY_CONFIG } from '../../context/CurrencyContext';

export default function BusinessSettings({
  reportCurrency,
  saveCurrency,
  tenantLogoUrl,
  setTenantLogoUrl,
  saveTenantLogo,
  tenantLogoSaving,
  brandName,
}) {
  return (
    /* data-settings-section="business" (REQUIRED — do not remove) */
    <Card className="border-slate-200" data-testid="business-settings-card" data-settings-section="business">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Settings2 size={20} className="text-slate-600" />
          Business Settings
        </CardTitle>
        <CardDescription>Reporting currency and branding shown on payslips & employee emails.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Reporting Currency */}
        <div className="max-w-sm">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">
            Reporting Currency
          </Label>
          <p className="text-xs text-slate-500 mb-3">
            Currency used for payroll and workforce reports. Does not affect billing.
          </p>
          <Select
            value={reportCurrency}
            onValueChange={async (code) => {
              await saveCurrency(code);
              toast.success(`Reporting currency updated to ${code}`);
            }}
          >
            <SelectTrigger className="h-10 w-full" data-testid="reporting-currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCY_CONFIG.map(c => (
                <SelectItem key={c.code} value={c.code} data-testid={`currency-opt-${c.code}`}>
                  <span className="font-semibold text-slate-700">{c.symbol}</span>
                  <span className="ml-2">{c.code} — {c.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Company Logo (for payslips + emails) */}
        {typeof setTenantLogoUrl === 'function' && (
          <div className="border-t border-slate-100 pt-6">
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon size={16} className="text-slate-500" />
              <Label className="text-sm font-medium text-slate-700">Company Logo</Label>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Public HTTPS image URL. Shown on the payslip PDF, the modal preview, and the
              automated payslip email above the brand name. Recommended: 200×64 px PNG/JPG hosted on a CDN.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
              <Input
                type="url"
                placeholder="https://cdn.example.com/my-logo.png"
                value={tenantLogoUrl || ''}
                onChange={(e) => setTenantLogoUrl(e.target.value)}
                className="flex-1 h-10"
                data-testid="tenant-logo-url-input"
              />
              <Button
                onClick={saveTenantLogo}
                disabled={tenantLogoSaving}
                className="h-10 shrink-0"
                data-testid="save-tenant-logo-btn"
              >
                {tenantLogoSaving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                {tenantLogoSaving ? 'Saving…' : 'Save logo'}
              </Button>
            </div>

            {/* Live preview */}
            {tenantLogoUrl && /^https?:\/\//i.test(tenantLogoUrl) && (
              <div className="mt-4 inline-flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50" data-testid="tenant-logo-preview">
                <img
                  src={tenantLogoUrl}
                  alt={(brandName || 'Logo') + ' preview'}
                  className="max-h-10 max-w-[140px] object-contain"
                  onError={(e) => { e.target.style.opacity = '0.3'; }}
                />
                <span className="text-xs text-slate-500">Preview</span>
              </div>
            )}
          </div>
        )}

      </CardContent>
    </Card>
  );
}
